/** Native course material and resource-placement client. */
import { commitOptions } from "./commitSeams";
import { api } from "@/lib/api/client";


export interface Material {
  reading_id?: string | null;
  id: string;
  course_id: string;
  /** PRD §9.3: the one teaching unit this material sits in, or none. */
  unit_id: string | null;
  week_no: number | null;
  title: string;
  file_url: string | null;
  file_size: number | null;
  material_type: string | null;
  google_drive_file_id: string | null;
  google_drive_resource_key: string | null;
  google_drive_mime_type: string | null;
  sort_order: number;
  /**
   * How many resource placements name this material, and how many are
   * published. Publication remains on the placement, not this record.
   */
  resource_placement_count: number;
  published_reference_count: number;
  /**
   * An earlier version of a corrected material: no placement names it and a
   * correction event does. It is history only, never listed again. Present on
   * the editor's read; a learner never receives such a row.
   */
  superseded?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateMaterial {
  week_no?: number | null;
  title: string;
  file_url?: string | null;
  file_size?: number | null;
  material_type?: string | null;
  google_drive_file_id?: string | null;
  google_drive_resource_key?: string | null;
  google_drive_mime_type?: string | null;
  sort_order?: number;
  unit_id?: string | null;
  /** "syllabus" creates the course's draft syllabus placement in the same write. */
  placement_role?: ResourcePlacementRole | null;
  publish?: boolean;
}

/**
 * Only the keys actually sent are written, so omitting `unit_id` leaves the
 * placement alone and sending `null` unplaces it.
 *
 * The server freezes the content half — title, file, size, type — once a
 * published resource placement names the material, and answers 409. The
 * correction path points the placement at a different material and records the swap in history a
 * learner can read.
 */
export interface PatchMaterial {
  unit_id?: string | null;
  week_no?: number | null;
  sort_order?: number;
  title?: string;
  file_url?: string | null;
  file_size?: number | null;
  material_type?: string | null;
  google_drive_file_id?: string | null;
  google_drive_resource_key?: string | null;
  google_drive_mime_type?: string | null;
  /**
   * The material version the edit was made against. A stale one changes
   * nothing and answers 409 `{message, current}`; without it the save is
   * unguarded.
   */
  expected_updated_at?: string;
}

/**
 * A published material's content change. The server copies the material with
 * these facts changed, points the published placement at the copy, and records
 * one correction learners read as "Updated {date}". `revision` is the
 * placement's; a stale one or a stale `expected_updated_at` answers 409
 * `{message, current}`.
 */
export interface CorrectMaterial {
  revision: number;
  expected_updated_at?: string;
  title?: string;
  file_url?: string | null;
  file_size?: number | null;
  material_type?: string | null;
  google_drive_file_id?: string | null;
  google_drive_resource_key?: string | null;
  google_drive_mime_type?: string | null;
}

export interface MaterialCorrection {
  material: Material;
  placement: ResourcePlacement;
}

export interface ReorderMaterial {
  id: string;
  expected_sort_order: number;
  sort_order: number;
}

export type ResourceKind = "material" | "assignment" | "announcement";
export type ResourcePlacementRole = "syllabus" | "course_content";

export interface ResourcePlacement {
  reading_id?: string | null;
  id: string;
  course_id: string;
  position: number | null;
  placement_role: ResourcePlacementRole;
  reference_kind: ResourceKind;
  material_id: string | null;
  assignment_id: string | null;
  announcement_id: string | null;
  display_label: string | null;
  publication_status: "draft" | "published";
  published_revision: number;
  revision: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  title: string | null;
  destination_url: string | null;
  target_state: "draft" | "published" | "closed" | "available" | "unavailable";
  /**
   * The named material's own metadata, so a learner sees what the resource is
   * and not only that something is there. Present on the list read; a write
   * response returns the reference columns alone, hence optional.
   */
  material_type?: string | null;
  file_size?: number | null;
  /**
   * The named material's Drive MIME type, when it is a picker-sourced reference.
   * Provenance for labelling only. The file ID and resource key are deliberately
   * not projected onto a reference a learner can read.
   */
  google_drive_mime_type?: string | null;
  unit_id?: string | null;
  week_no?: number | null;
  sort_order?: number | null;
}

export interface ResourcePlacementEvent {
  event_no: number;
  event_kind: "published" | "material_correction" | "placement_change" | "label_change";
  changed_fields: string[];
  previous_material_id: string | null;
  material_id: string | null;
  created_at: string;
}

export interface CreateResourcePlacement {
  reference_kind: ResourceKind;
  placement_role?: ResourcePlacementRole;
  position?: number | null;
  material_id?: string | null;
  assignment_id?: string | null;
  announcement_id?: string | null;
  display_label?: string | null;
  publication_status?: "draft" | "published";
}

export interface PatchResourcePlacement extends Partial<Omit<CreateResourcePlacement, "reference_kind">> {
  revision: number;
}

export interface MaterialLibraryRead {
  can_publish?: boolean;
  visible_placement_ids?: string[];
  materials: Material[];
  placements: ResourcePlacement[];
  read_at: string;
  ranking: { state: "empty" | "unavailable" } | { state: "ready"; value: { candidates: Array<{ id: string; eligible: boolean; blocked: boolean; rank: number | null }>; lead: string | null } };
}

export const courseMaterialsApi = {

  /**
   * The course material library for an editor, and for a learner exactly the
   * materials a published resource placement names. The narrowing is the
   * server's (migration 063 plus the route), not this client's.
   */
  getMaterials: (courseId: string) => api.get<Material[]>(`/courses/${courseId}/materials`),
  getLibrary: (courseId: string, query = "") => api.get<MaterialLibraryRead>(`/courses/${courseId}/material-library${query ? `?q=${encodeURIComponent(query)}` : ""}`),
  createMaterial: (courseId: string, body: CreateMaterial, key?: string) =>
    key ? api.post<Material>(`/courses/${courseId}/materials`, body, commitOptions(key)) : api.post<Material>(`/courses/${courseId}/materials`, body),
  patchMaterial: (courseId: string, materialId: string, body: PatchMaterial, key?: string) =>
    key ? api.patch<Material>(`/courses/${courseId}/materials/${materialId}`, body, commitOptions(key)) : api.patch<Material>(`/courses/${courseId}/materials/${materialId}`, body),
  correctMaterial: (courseId: string, materialId: string, body: CorrectMaterial, key?: string) =>
    key ? api.post<MaterialCorrection>(`/courses/${courseId}/materials/${materialId}/correction`, body, commitOptions(key)) : api.post<MaterialCorrection>(`/courses/${courseId}/materials/${materialId}/correction`, body),
  reorderMaterials: (courseId: string, materials: ReorderMaterial[]) =>
    api.put<Material[]>(`/courses/${courseId}/materials/order`, { materials }),
  publishMaterial: (courseId: string, materialId: string, body: PatchMaterial & { revision: number | null; placement_role: ResourcePlacementRole }, key: string) =>
    api.post<MaterialCorrection>(`/courses/${courseId}/materials/${materialId}/publish`, body, commitOptions(key)),
  deleteMaterial: (courseId: string, materialId: string, key?: string, version?: string) =>
    key ? api.delete<void>(`/courses/${courseId}/materials/${materialId}`, { expected_updated_at: version }, commitOptions(key)) : api.delete<void>(`/courses/${courseId}/materials/${materialId}`),

  getResourcePlacements: (courseId: string) =>
    api.get<ResourcePlacement[]>(`/courses/${courseId}/resource-placements`),
  createResourcePlacement: (courseId: string, body: CreateResourcePlacement) =>
    api.post<ResourcePlacement>(`/courses/${courseId}/resource-placements`, body),
  patchResourcePlacement: (
    courseId: string,
    placementId: string,
    body: PatchResourcePlacement,
  ) => api.patch<ResourcePlacement>(
    `/courses/${courseId}/resource-placements/${placementId}`,
    body,
  ),
  deleteResourcePlacement: (courseId: string, placementId: string) =>
    api.delete<void>(`/courses/${courseId}/resource-placements/${placementId}`),
  reorderResourcePlacements: (
    courseId: string,
    placements: Array<{ id: string; revision: number; position: number }>,
  ) => api.put<ResourcePlacement[]>(
    `/courses/${courseId}/resource-placements/order`,
    { placements },
  ),
  getResourcePlacementHistory: (courseId: string, placementId: string) =>
    api.get<ResourcePlacementEvent[]>(
      `/courses/${courseId}/resource-placements/${placementId}/history`,
    ),
};
